import { api } from 'app/app.api';

import {
  DeleteUserStorageApiArg,
  DeleteUserStorageApiResponse,
  ReadUserStorageApiArg,
  ReadUserStorageApiResponse,
  SetUserStorageApiArg,
  SetUserStorageApiResponse,
} from './types/userStorageApi.types';
import {
  cutoffDateForNewAgentsDefaults,
  summaryVisibilityStateDefault,
  summaryVisibilityStateDefaultAfterCutoffDate,
  translationVisibilityStateDefault,
  translationVisibilityStateDefaultAfterCutoffDate,
} from './userStorage.constants';
import { transformReadUserStorageResponse } from './utils/transformResponse';

export const userStorageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readUserStorage: builder.query<
      ReadUserStorageApiResponse,
      ReadUserStorageApiArg
    >({
      query: ({ keys = [] }) => ({
        version: 4,
        path: 'user-storage',
        params: { keys },
      }),
      transformResponse: transformReadUserStorageResponse,
      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        try {
          const response = await queryFulfilled;
          const copilotData = response.data.agent_copilot;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const state = getState() as any;
          const userCreatedValue = state.settings?.profile?.profile?.created_at;
          const userCreatedDate = new Date(userCreatedValue || 0);

          const isAfterCutoffDate =
            userCreatedDate.getTime() >
            cutoffDateForNewAgentsDefaults.getTime();

          const hasSummaryVisibilityState = !!(
            copilotData && copilotData['summaryVisibilityState']
          );
          const hasTranslationVisibilityState = !!(
            copilotData && copilotData['translationVisibilityState']
          );

          if (!hasSummaryVisibilityState || !hasTranslationVisibilityState) {
            // set default based on user creation date
            dispatch(
              userStorageApi.util.updateQueryData(
                'readUserStorage',
                {},
                (draft) => {
                  draft.agent_copilot = {
                    ...draft.agent_copilot,
                    ...(!hasSummaryVisibilityState && {
                      summaryVisibilityState: isAfterCutoffDate
                        ? summaryVisibilityStateDefaultAfterCutoffDate
                        : summaryVisibilityStateDefault,
                    }),
                    ...(!hasTranslationVisibilityState && {
                      translationVisibilityState: isAfterCutoffDate
                        ? translationVisibilityStateDefaultAfterCutoffDate
                        : translationVisibilityStateDefault,
                    }),
                  };
                },
              ),
            );
          }
        } catch {}
      },
    }),
    setUserStorage: builder.mutation<
      SetUserStorageApiResponse,
      SetUserStorageApiArg
    >({
      query: (body) => ({
        version: 4,
        path: 'user-storage',
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(patch, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userStorageApi.util.updateQueryData(
            'readUserStorage',
            {},
            (draft) => {
              Object.entries(patch).forEach(([key, value]) => {
                if (value === null) {
                  delete draft[key];
                } else {
                  draft[key] = value;
                }
              });
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeUserStorageByKey: builder.mutation<
      DeleteUserStorageApiResponse,
      DeleteUserStorageApiArg
    >({
      query: ({ key }) => ({
        version: 4,
        path: `user-storage/${key}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ key }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userStorageApi.util.updateQueryData(
            'readUserStorage',
            {},
            (draft) => {
              delete draft[key];
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});
